import { faMagnifyingGlass, faSort, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useOnClickOutside } from '~/utils/hooks/useOnClickOutside'

/** Navigation for the Sell tab, modelled on the Inventory page.
 *
 *  Deliberately its own components rather than importing `features/stake`'s:
 *  those read their state from `StakeListContext` rather than props, so reusing
 *  them literally would mean refactoring a page that is live and working. The
 *  shapes, the breakpoints and the reasoning are lifted wholesale — see
 *  `features/stake/components/CategoryTabs.tsx`, whose comments record why the
 *  tabs wrap into a grid below `lg` and why search and sort share a row on
 *  mobile. When `/trade` replaces `/marketplace`, these should be unified into
 *  one shared set and both features should use it. */

export type SellSort = 'id' | '-id' | 'rank' | '-rank'

const SORTS: Array<{ value: SellSort; label: string; short: string }> = [
    { value: 'id', label: 'ID (Low to High)', short: 'ID ↑' },
    { value: '-id', label: 'ID (High to Low)', short: 'ID ↓' },
    { value: 'rank', label: 'Rank (Best first)', short: 'Rank ↑' },
    { value: '-rank', label: 'Rank (Worst first)', short: 'Rank ↓' },
]

const CATEGORIES: Array<{ value: string; label: string }> = [
    { value: 'ape', label: 'Apes' },
    { value: 'droid', label: 'Droids' },
    { value: 'lootbox', label: 'Loot Boxes' },
    { value: 'weapon', label: 'Weapons' },
]

type Counts = Record<string, number>

export const CategoryTabs = ({
    counts,
    value,
    onChange,
}: {
    counts: Counts
    value: string
    onChange: (v: string) => void
}) => (
    // Two columns until lg, where all five fit on one row without compressing.
    // A horizontal scroll clipped the labels mid-word and gave no hint it
    // scrolled, which is the mistake the Inventory page already made and fixed.
    <div className='grid grid-cols-2 gap-2 lg:flex lg:flex-row'>
        <Tab
            className='col-span-2 lg:col-span-1'
            label='All items'
            count={counts.all ?? 0}
            selected={value === 'all'}
            onSelect={() => onChange('all')}
        />
        {CATEGORIES.map((c) => (
            <Tab
                key={c.value}
                label={c.label}
                count={counts[c.value] ?? 0}
                selected={value === c.value}
                onSelect={() => onChange(c.value)}
            />
        ))}
    </div>
)

const Tab = ({
    label,
    count,
    selected,
    onSelect,
    className,
}: {
    label: string
    count: number
    selected: boolean
    onSelect: () => void
    className?: string
}) => (
    <button
        onClick={onSelect}
        className={twMerge(
            'flex flex-1 flex-col items-start border px-4 py-3 text-left transition-colors',
            selected
                ? 'border-[#03CEFF] bg-white/[0.06]'
                : 'border-white/10 hover:border-white/25',
            className
        )}
    >
        <span className='text-lg font-black leading-none text-white'>{count}</span>
        <span className='mt-1 text-[10px] uppercase tracking-widest text-white/50'>{label}</span>
    </button>
)

export const SellControls = ({
    search,
    onSearch,
    sort,
    onSort,
    listableOnly,
    onListableOnly,
}: {
    search: string
    onSearch: (v: string) => void
    sort: SellSort
    onSort: (v: SellSort) => void
    listableOnly: boolean
    onListableOnly: (v: boolean) => void
}) => (
    <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4'>
        {/* Search and sort share a row on mobile: two short controls stacked
            wasted a band of vertical space above the grid. */}
        <div className='flex flex-row gap-3 lg:contents'>
            <Search value={search} onChange={onSearch} />
            <Sort value={sort} onChange={onSort} />
        </div>
        <ListableFilter value={listableOnly} onChange={onListableOnly} />
    </div>
)

const Search = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
    const [input, setInput] = useState(value)

    // Debounced: every keystroke is a request now that filtering is server-side.
    useEffect(() => {
        const timer = setTimeout(() => onChange(input), 300)
        return () => clearTimeout(timer)
    }, [input])

    return (
        <label className='flex min-h-[2.75rem] min-w-0 flex-1 flex-row items-center gap-3 rounded border border-white/20 p-2 px-4 focus-within:border-[#03CEFF] lg:max-w-xs'>
            <FontAwesomeIcon icon={faMagnifyingGlass} className='text-white/40' />
            <input
                type='text'
                inputMode='numeric'
                placeholder='Search by ID'
                className='min-w-0 flex-1 bg-transparent font-bold outline-none placeholder:text-white/20'
                value={input}
                onChange={(e) => setInput(e.target.value)}
            />
            {input && (
                <button onClick={() => setInput('')} aria-label='Clear search'>
                    <FontAwesomeIcon icon={faXmark} className='text-white/40 hover:text-white' />
                </button>
            )}
        </label>
    )
}

const Sort = ({ value, onChange }: { value: SellSort; onChange: (v: SellSort) => void }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    useOnClickOutside(ref, () => setOpen(false))

    const current = SORTS.find((s) => s.value === value) ?? SORTS[0]

    return (
        <div ref={ref} className='relative'>
            <button
                onClick={() => setOpen((o) => !o)}
                className='flex min-h-[2.75rem] w-full flex-row items-center gap-2 whitespace-nowrap rounded border border-white/20 px-4 font-bold hover:border-white/40'
            >
                <FontAwesomeIcon icon={faSort} className='text-white/40' />
                <span className='lg:hidden'>{current.short}</span>
                <span className='hidden lg:inline'>{current.label}</span>
            </button>
            {open && (
                <div className='absolute z-20 mt-1 w-56 border border-white/20 bg-black'>
                    {SORTS.map((s) => (
                        <button
                            key={s.value}
                            onClick={() => {
                                onChange(s.value)
                                setOpen(false)
                            }}
                            className={twMerge(
                                'block w-full px-4 py-2 text-left text-sm hover:bg-white/10',
                                s.value === value && 'text-[#03CEFF]'
                            )}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

const ListableFilter = ({
    value,
    onChange,
}: {
    value: boolean
    onChange: (v: boolean) => void
}) => (
    <div className='flex flex-row border border-white/20'>
        {[
            { label: 'All', on: false },
            { label: 'Listable', on: true },
        ].map((o) => (
            <button
                key={o.label}
                onClick={() => onChange(o.on)}
                className={twMerge(
                    'min-h-[2.75rem] flex-1 px-4 text-xs font-black uppercase tracking-widest transition-colors lg:flex-none',
                    value === o.on ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'
                )}
            >
                {o.label}
            </button>
        ))}
    </div>
)
